import {
  PathCreatorResourceFragment,
  usePathCreatorResourcesQuery
} from "../../api/generated"
import ItemSelector, { ItemSelectorProps } from "./ItemSelector"
import { registrationRoleIntMap } from "./RoleBadge"
import { SelectedResource } from "./state"
import useRandomThemeResourceImage from "./useRandomThemeResourceImage"

// Resources licensed from other schools don't have a source cohort,
// at least, not one that is linked to the licensee school.
// The PathCreatorFlow currently doesn't have a method for handling
// these resources yet.
export type SelectedResourceWithSourceCohort = PathCreatorResourceFragment & {
  source_cohort: NonNullable<PathCreatorResourceFragment["source_cohort"]>
}

// Maps the PathCreatorResourceFragment to the SelectedResource type.
export function massageResource(
  resource: SelectedResourceWithSourceCohort
): SelectedResource {
  const role = (
    registrationRoleIntMap.get(resource.source_cohort?.registration?.role!) ||
    null
  )?.enum as SelectedResource["role"]

  return {
    id: resource.id,
    name: resource.name,
    image: resource.image,
    background_image: resource.background_image,
    role,
    sourceCohort: {
      id: resource.source_cohort.id,
      role
    }
  }
}

type ResourceSelectorProps = Omit<
  ItemSelectorProps<SelectedResource>,
  "options" | "isLoading" | "itemLabel" | "getNewOptionData"
>

function ResourceSelector({
  value,
  onChange,
  ...itemSelectorProps
}: ResourceSelectorProps) {
  const query = usePathCreatorResourcesQuery()
  const resources =
    query.data?.resources?.edges
      ?.map((edge) => edge?.node!)
      .reduce((acc, node) => {
        // Narrow the list down to only those resources that have a soure cohort.
        if (node.source_cohort) {
          acc.push(massageResource(node as SelectedResourceWithSourceCohort))
        }
        return acc
      }, [] as SelectedResource[]) || []

  // Selecting resource when that is the status or we
  // have a selected resource.
  const [defaultImage, image, burredImage] = useRandomThemeResourceImage()

  // "Create a new Path"
  const createOption = {
    id: -1,
    name: "",
    image: image,
    background_image: burredImage,
    sourceCohort: {
      id: -1,
      role: null
    }
  }

  return (
    <ItemSelector
      itemLabel="Path"
      icon={"path"}
      options={resources}
      value={value?.id ? value : null}
      onChange={onChange}
      isLoading={query.loading}
      getNewOptionData={(inputValue, optionLabel) => ({
        ...createOption,
        name: inputValue
      })}
      // We blanketly deny creating a new resource using the select menu.
      isValidNewOption={() => false}
      {...itemSelectorProps}
    />
  )
}

export default ResourceSelector
